import { MinusSignIcon, Tick02Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Checkbox as CheckboxPrimitive, RadioGroup as RadioPrimitive, Switch as SwitchPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { className, label, description, id, ...props },
  ref,
) {
  const autoId = React.useId();
  const cid = id ?? autoId;
  const descId = description ? `${cid}-desc` : undefined;
  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={cid}
      aria-describedby={descId}
      className={cn(
        'peer inline-flex size-5 shrink-0 items-center justify-center rounded-[5px] border border-border-control bg-surface-default',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'data-[state=checked]:border-brand-primary data-[state=checked]:bg-brand-primary data-[state=checked]:text-text-on-primary',
        'data-[state=indeterminate]:border-brand-primary data-[state=indeterminate]:bg-brand-primary data-[state=indeterminate]:text-text-on-primary',
        'disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {props.checked === 'indeterminate' ? <Icon icon={MinusSignIcon} className="size-3.5" aria-hidden /> : <Icon icon={Tick02Icon} className="size-3.5" aria-hidden />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">{box}</div>
      <div className="grid gap-0.5">
        <label htmlFor={cid} className="text-body font-medium text-text-primary peer-disabled:opacity-50">
          {label}
        </label>
        {description ? (
          <p id={descId} className="text-meta text-text-secondary">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, label, description, id, ...props },
  ref,
) {
  const autoId = React.useId();
  const sid = id ?? autoId;
  const descId = description ? `${sid}-desc` : undefined;
  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={sid}
      aria-describedby={descId}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-pill border border-border-control bg-surface-subtle p-0.5 transition-colors duration-[var(--ef-motion-fast)]',
        'data-[state=checked]:border-brand-primary data-[state=checked]:bg-brand-primary',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-[18px] rounded-pill bg-border-control shadow-card transition-transform duration-[var(--ef-motion-fast)] data-[state=checked]:translate-x-5 data-[state=checked]:bg-surface-default" />
    </SwitchPrimitive.Root>
  );
  if (!label) return control;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="grid gap-0.5">
        <label htmlFor={sid} className="text-body font-medium text-text-primary">
          {label}
        </label>
        {description ? (
          <p id={descId} className="text-meta text-text-secondary">
            {description}
          </p>
        ) : null}
      </div>
      {control}
    </div>
  );
});

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps extends Omit<React.ComponentPropsWithoutRef<typeof RadioPrimitive.Root>, 'children'> {
  options: readonly RadioOption[];
  /** Layout direction of the options. */
  orientation?: 'vertical' | 'horizontal';
}

/** Radio group (Radix): arrow keys move between options. */
export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  { options, className, orientation = 'vertical', ...props },
  ref,
) {
  const baseId = React.useId();
  return (
    <RadioPrimitive.Root
      ref={ref}
      orientation={orientation}
      className={cn(orientation === 'vertical' ? 'grid gap-3' : 'flex flex-wrap gap-4', className)}
      {...props}
    >
      {options.map((o, i) => {
        const oid = `${baseId}-${i}`;
        return (
          <div key={o.value} className="flex items-start gap-3">
            <RadioPrimitive.Item
              id={oid}
              value={o.value}
              disabled={o.disabled}
              aria-describedby={o.description ? `${oid}-desc` : undefined}
              className={cn(
                'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-pill border border-border-control bg-surface-default',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                'data-[state=checked]:border-brand-primary disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              <RadioPrimitive.Indicator className="block size-2.5 rounded-pill bg-brand-primary" />
            </RadioPrimitive.Item>
            <div className="grid gap-0.5">
              <label htmlFor={oid} className="text-body font-medium text-text-primary">
                {o.label}
              </label>
              {o.description ? (
                <p id={`${oid}-desc`} className="text-meta text-text-secondary">
                  {o.description}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </RadioPrimitive.Root>
  );
});
